import * as Sentry from '@sentry/react';
import packageJson from '../../package.json';
import { currentEnvironment } from '../helper/currentEnvironment';

/**
 * Sets user information for Sentry tracking
 * @param {Object|null} user - The user object containing id, email, etc. Pass null to clear user context
 */
export const setSentryUser = user => {
  // eslint-disable-next-line no-console
  console.log(' user:', user);

  if (!user) {
    // Clear user context if no user is provided
    Sentry.setUser(null);
    return;
  }

  // Set user context with available information
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || user.name,
  });
};

/**
 * Initializes Sentry error tracking
 * This function sets up Sentry with our configuration and error handling logic
 */
export const initSentry = () => {
  Sentry.init({
    // The DSN (Data Source Name) tells Sentry where to send events
    // Should be stored in environment variables for security
    dsn: process.env.REACT_APP_SENTRY_DSN,

    // Array of integrations to add additional functionality
    // Can include things like BrowserTracing, Replay, etc.
    integrations: [],

    // Specifies the environment (development/production)
    // Used to separate events by environment in Sentry dashboard
    environment: currentEnvironment(),

    // Release version for tracking errors across different versions
    // Format: 'project-name@version'
    release: 'aliments-buyer-web@' + packageJson.version,

    // Whether Sentry should be active
    // true = always send errors, false = never send errors
    enabled: true,

    // Whether to include stack traces with errors
    // Helps with debugging by showing where errors occurred
    attachStacktrace: true,

    // Enables verbose logging to console
    // Useful for debugging Sentry itself
    debug: true,

    // Maximum number of breadcrumbs to record
    // Breadcrumbs are logs of events leading up to an error
    maxBreadcrumbs: 50,

    // Percentage of transactions to sample (0.0 to 1.0)
    // Lower values reduce the volume of data sent to Sentry
    tracesSampleRate: 0.2,

    /**
     * Function called before sending events to Sentry
     * Used for filtering, modifying, or rate limiting events
     * @param {Object} event - The error event to be sent
     * @param {Object} hint - Contains additional information about the event
     * @returns {Object|null} The event to send, or null to not send
     */
    beforeSend(event, hint) {
      // Rate limiting implementation
      // Ensures the same error is not reported more than once per second
      if (hint && hint.originalException) {
        const error = hint.originalException;
        // Create unique key from error details
        const key = `${error.name}:${error.message}`;
        const now = Date.now();

        // Get the last time this error was sent, defaulting to 0
        const sentryLastSent = window.__sentryLastSent || {};
        const lastSent = sentryLastSent[key] || 0;

        // If the same error was sent less than 1 second ago, don't send it
        if (now - lastSent < 1000) {
          return null;
        }

        // Update the timestamp for when this error was last sent
        window.__sentryLastSent = {
          ...sentryLastSent,
          [key]: now,
        };
      }

      // Return the event to be sent to Sentry
      return event;
    },
  });
};
